import { randomUUID } from 'node:crypto';
import { readFile, mkdir, stat, access } from 'node:fs/promises';
import { join, resolve, relative, isAbsolute } from 'node:path';
import { createLogger } from '../logging/logger.js';
import { cloneRepository } from '../plugin/git-clone.js';
import { createStoreId } from '../types/brands.js';
import { ok, err } from '../types/result.js';
import { CURRENT_SCHEMA_VERSION } from '../types/store.js';
import { atomicWriteFile } from '../utils/atomic-write.js';
import type { GitignoreService } from './gitignore.service.js';
import type { StoreDefinitionService } from './store-definition.service.js';
import type { StoreId } from '../types/brands.js';
import type { Result } from '../types/result.js';
import type {
  StoreDefinition,
  FileStoreDefinition,
  RepoStoreDefinition,
  WebStoreDefinition,
  IngestConfig,
} from '../types/store-definition.js';
import type { Store, FileStore, RepoStore, WebStore, StoreType, PathType } from '../types/store.js';

const logger = createLogger('store-service');

/**
 * Check if a file exists
 */
async function fileExists(path: string): Promise<boolean> {
  try {
    await access(path);
    return true;
  } catch {
    return false;
  }
}

export interface CreateStoreInput {
  name: string;
  type: StoreType;
  path?: string | undefined;
  url?: string | undefined;
  description?: string | undefined;
  tags?: string[] | undefined;
  branch?: string | undefined;
  depth?: number | undefined;
  // Ingest configuration for file/repo stores
  ingest?: IngestConfig | undefined;
  // Web store crawl options
  maxPages?: number | undefined;
  crawlInstructions?: string | undefined;
  extractInstructions?: string | undefined;
}

export interface StoreServiceOptions {
  /** Optional definition service for auto-updating git-committable config */
  definitionService?: StoreDefinitionService;
  /** Optional gitignore service for ensuring .gitignore patterns */
  gitignoreService?: GitignoreService;
  /** Optional project root for resolving relative paths */
  projectRoot?: string;
  /** Embedding model ID for provenance tracking (required for new stores) */
  embeddingModelId: string;
}

export interface OperationOptions {
  /** Skip syncing to store definitions (used by stores:sync command) */
  skipDefinitionSync?: boolean;
}

interface StoreRegistry {
  stores: Store[];
}

export class StoreService {
  private readonly dataDir: string;
  private readonly definitionService: StoreDefinitionService | undefined;
  private readonly gitignoreService: GitignoreService | undefined;
  private readonly projectRoot: string | undefined;
  private readonly embeddingModelId: string;
  private registry: StoreRegistry = { stores: [] };
  private registryMtime: number = 0;
  /** Flag for lazy schema migration - bump to v3 on next save */
  private needsMigration: boolean = false;

  constructor(dataDir: string, options: StoreServiceOptions) {
    this.dataDir = dataDir;
    this.definitionService = options.definitionService ?? undefined;
    this.gitignoreService = options.gitignoreService ?? undefined;
    this.projectRoot = options.projectRoot ?? undefined;
    this.embeddingModelId = options.embeddingModelId;
  }

  /**
   * Get the current embedding model ID used for new stores.
   * Used by model compatibility validation.
   */
  getCurrentModelId(): string {
    return this.embeddingModelId;
  }

  /**
   * Convert an absolute path to stored format.
   * Returns relative path + pathType if inside projectRoot, otherwise absolute.
   *
   * @param absolutePath - The absolute path to convert
   * @param isClonedRepo - If true, always store absolute (managed by BK under dataDir)
   */
  private toStoredPath(
    absolutePath: string,
    isClonedRepo: boolean = false
  ): { path: string; pathType: PathType } {
    // Cloned repos are managed under dataDir - always absolute
    if (isClonedRepo) {
      return { path: absolutePath, pathType: 'absolute' };
    }

    // No projectRoot - can't make relative
    if (this.projectRoot === undefined) {
      return { path: absolutePath, pathType: 'absolute' };
    }

    // Check if path is inside projectRoot
    const relativePath = relative(this.projectRoot, absolutePath);

    // If relative path starts with ".." or is absolute, it's outside projectRoot
    if (relativePath.startsWith('..') || isAbsolute(relativePath)) {
      logger.info(`Path outside projectRoot, storing absolute: ${absolutePath}`);
      return { path: absolutePath, pathType: 'absolute' };
    }

    logger.debug(`Storing relative path: ${relativePath} (resolved from ${absolutePath})`);
    return { path: relativePath, pathType: 'relative' };
  }

  /**
   * Resolve a stored path to absolute runtime path.
   * Handles both relative (v3+) and absolute (v2 and earlier) paths.
   *
   * @param storedPath - The path as stored in stores.json
   * @param pathType - The path type (undefined for pre-v3 stores, treated as absolute)
   */
  private resolveStoredPath(storedPath: string, pathType: PathType | undefined): string {
    // Pre-v3 stores or explicit absolute paths
    if (pathType === undefined || pathType === 'absolute') {
      return storedPath;
    }

    // Relative path requires projectRoot
    if (this.projectRoot === undefined) {
      logger.error(`Store has relative path but no projectRoot: ${storedPath}`);
      // Return as-is - will fail at file access, caught by health check
      return storedPath;
    }

    const resolved = resolve(this.projectRoot, storedPath);
    logger.debug(`Resolved relative path: ${storedPath} → ${resolved}`);
    return resolved;
  }

  async initialize(): Promise<void> {
    await mkdir(this.dataDir, { recursive: true });
    await this.loadRegistry();
  }

  /**
   * Reload registry from disk if it has been modified by another process (e.g., CLI).
   * This enables MCP server to see stores created by CLI without restart.
   */
  private async ensureRegistryFresh(): Promise<void> {
    const registryPath = join(this.dataDir, 'stores.json');
    try {
      const stats = await stat(registryPath);
      if (stats.mtimeMs > this.registryMtime) {
        await this.loadRegistry();
      }
    } catch {
      // File doesn't exist yet, that's ok
    }
  }

  /**
   * Convert a Store and CreateStoreInput to a StoreDefinition for persistence.
   * Returns undefined for stores that shouldn't be persisted (e.g., local repo stores).
   */
  private createDefinitionFromStore(
    store: Store,
    input: CreateStoreInput
  ): StoreDefinition | undefined {
    // Copy tags array to convert from readonly to mutable
    const tags = store.tags !== undefined ? [...store.tags] : undefined;
    const base = {
      name: store.name,
      description: store.description,
      tags,
    };

    switch (store.type) {
      case 'file': {
        const fileStore = store;
        const fileDef: FileStoreDefinition = {
          ...base,
          type: 'file',
          // Use original input path if provided (may be relative), otherwise use normalized
          path: input.path ?? fileStore.path,
          pathType: fileStore.pathType,
        };
        return fileDef;
      }
      case 'repo': {
        const repoStore = store;
        // Local repo stores (no URL) are machine-specific; skip definition sync
        if (repoStore.url === undefined) {
          return undefined;
        }
        const repoDef: RepoStoreDefinition = {
          ...base,
          type: 'repo',
          url: repoStore.url,
          branch: repoStore.branch,
          depth: input.depth,
        };
        return repoDef;
      }
      case 'web': {
        const webStore = store;
        const webDef: WebStoreDefinition = {
          ...base,
          type: 'web',
          url: webStore.url,
          depth: webStore.depth,
          maxPages: input.maxPages,
          crawlInstructions: input.crawlInstructions,
          extractInstructions: input.extractInstructions,
        };
        return webDef;
      }
    }
  }

  /**
   * Create a StoreDefinition from an existing store (without original input).
   * Used when updating/renaming stores where we don't have the original input.
   * Returns undefined for stores that shouldn't be persisted (e.g., local repo stores).
   */
  private createDefinitionFromExistingStore(store: Store): StoreDefinition | undefined {
    // Copy tags array to convert from readonly to mutable
    const tags = store.tags !== undefined ? [...store.tags] : undefined;
    const base = {
      name: store.name,
      description: store.description,
      tags,
    };

    switch (store.type) {
      case 'file': {
        const fileDef: FileStoreDefinition = {
          ...base,
          type: 'file',
          path: store.path,
          pathType: store.pathType,
        };
        return fileDef;
      }
      case 'repo': {
        // Local repo stores (no URL) are machine-specific; skip definition sync
        if (store.url === undefined) {
          return undefined;
        }
        const repoDef: RepoStoreDefinition = {
          ...base,
          type: 'repo',
          url: store.url,
          branch: store.branch,
          depth: store.depth,
        };
        return repoDef;
      }
      case 'web': {
        const webDef: WebStoreDefinition = {
          ...base,
          type: 'web',
          url: store.url,
          depth: store.depth,
          maxPages: store.maxPages,
          crawlInstructions: store.crawlInstructions,
          extractInstructions: store.extractInstructions,
        };
        return webDef;
      }
    }
  }

  async create(input: CreateStoreInput, options?: OperationOptions): Promise<Result<Store>> {
    if (!input.name || input.name.trim() === '') {
      return err(new Error('Store name cannot be empty'));
    }

    const existing = await this.getByName(input.name);
    if (existing !== undefined) {
      return err(new Error(`Store with name "${input.name}" already exists`));
    }

    const id = createStoreId(randomUUID());
    const now = new Date();

    let store: Store;

    switch (input.type) {
      case 'file': {
        if (input.path === undefined) {
          return err(new Error('Path is required for file stores'));
        }
        // Normalize path to absolute path, using projectRoot if available
        const normalizedPath =
          this.projectRoot !== undefined
            ? resolve(this.projectRoot, input.path)
            : resolve(input.path);
        // Validate directory exists
        try {
          const stats = await stat(normalizedPath);
          if (!stats.isDirectory()) {
            return err(new Error(`Path is not a directory: ${normalizedPath}`));
          }
        } catch {
          return err(new Error(`Directory does not exist: ${normalizedPath}`));
        }
        // Determine pathType for storage (resolved at save time, but track intent)
        const { pathType } = this.toStoredPath(normalizedPath, false);
        store = {
          type: 'file',
          id,
          name: input.name,
          path: normalizedPath,
          pathType,
          ingest: input.ingest,
          description: input.description,
          tags: input.tags,
          status: 'ready',
          createdAt: now,
          updatedAt: now,
          schemaVersion: CURRENT_SCHEMA_VERSION,
          modelId: this.embeddingModelId,
        } satisfies FileStore;
        break;
      }

      case 'repo': {
        let repoPath = input.path;

        // If URL provided, clone it
        if (input.url !== undefined) {
          const cloneDir = join(this.dataDir, 'repos', id);
          const result = await cloneRepository({
            url: input.url,
            targetDir: cloneDir,
            ...(input.branch !== undefined ? { branch: input.branch } : {}),
            depth: input.depth ?? 1,
            partialClone: input.ingest?.partialClone ?? false,
          });

          if (!result.success) {
            return err(result.error);
          }
          repoPath = result.data;
        }

        if (repoPath === undefined) {
          return err(new Error('Path or URL required for repo stores'));
        }

        // Normalize path to absolute path, using projectRoot if available
        const normalizedRepoPath =
          this.projectRoot !== undefined ? resolve(this.projectRoot, repoPath) : resolve(repoPath);

        // Validate local repo path exists (only for local repos without URL)
        if (input.url === undefined) {
          try {
            const stats = await stat(normalizedRepoPath);
            if (!stats.isDirectory()) {
              return err(new Error(`Path is not a directory: ${normalizedRepoPath}`));
            }
          } catch {
            return err(new Error(`Repository path does not exist: ${normalizedRepoPath}`));
          }
        }

        // Cloned repos (with URL) stay absolute, local repos (no URL) can be relative
        const isClonedRepo = input.url !== undefined;
        const { pathType: repoPathType } = this.toStoredPath(normalizedRepoPath, isClonedRepo);
        store = {
          type: 'repo',
          id,
          name: input.name,
          path: normalizedRepoPath,
          pathType: repoPathType,
          url: input.url,
          branch: input.branch,
          depth: input.depth ?? 1,
          ingest: input.ingest,
          description: input.description,
          tags: input.tags,
          status: 'ready',
          createdAt: now,
          updatedAt: now,
          schemaVersion: CURRENT_SCHEMA_VERSION,
          modelId: this.embeddingModelId,
        } satisfies RepoStore;
        break;
      }

      case 'web':
        if (input.url === undefined) {
          return err(new Error('URL is required for web stores'));
        }
        store = {
          type: 'web',
          id,
          name: input.name,
          url: input.url,
          depth: input.depth ?? 1,
          maxPages: input.maxPages,
          crawlInstructions: input.crawlInstructions,
          extractInstructions: input.extractInstructions,
          description: input.description,
          tags: input.tags,
          status: 'ready',
          createdAt: now,
          updatedAt: now,
          schemaVersion: CURRENT_SCHEMA_VERSION,
          modelId: this.embeddingModelId,
        } satisfies WebStore;
        break;

      default: {
        // Exhaustive check - if this is reached, input.type is invalid
        const invalidType: never = input.type;
        return err(new Error(`Invalid store type: ${String(invalidType)}`));
      }
    }

    this.registry.stores.push(store);
    await this.saveRegistry();

    // Ensure .gitignore has required patterns
    if (this.gitignoreService !== undefined) {
      await this.gitignoreService.ensureGitignorePatterns();
    }

    // Sync to store definitions if service is available and not skipped
    if (this.definitionService !== undefined && options?.skipDefinitionSync !== true) {
      const definition = this.createDefinitionFromStore(store, input);
      // Only add if definition was created (local repo stores return undefined)
      if (definition !== undefined) {
        await this.definitionService.addDefinition(definition);
      }
    }

    return ok(store);
  }

  async list(type?: StoreType): Promise<Store[]> {
    await this.ensureRegistryFresh();
    if (type !== undefined) {
      return this.registry.stores.filter((s) => s.type === type);
    }
    return [...this.registry.stores];
  }

  async get(id: StoreId): Promise<Store | undefined> {
    await this.ensureRegistryFresh();
    return this.registry.stores.find((s) => s.id === id);
  }

  async getByName(name: string): Promise<Store | undefined> {
    await this.ensureRegistryFresh();
    return this.registry.stores.find((s) => s.name === name);
  }

  async getByIdOrName(idOrName: string): Promise<Store | undefined> {
    await this.ensureRegistryFresh();
    return this.registry.stores.find((s) => s.id === idOrName || s.name === idOrName);
  }

  async update(
    id: StoreId,
    updates: Partial<Pick<Store, 'name' | 'description' | 'tags'>>,
    options?: OperationOptions
  ): Promise<Result<Store>> {
    const index = this.registry.stores.findIndex((s) => s.id === id);
    if (index === -1) {
      return err(new Error(`Store not found: ${id}`));
    }

    const store = this.registry.stores[index];
    if (store === undefined) {
      return err(new Error(`Store not found: ${id}`));
    }

    // Validate name is not empty when provided
    if (updates.name?.trim() === '') {
      return err(new Error('Store name cannot be empty'));
    }

    // Check for duplicate name when renaming
    const isRenaming = updates.name !== undefined && updates.name !== store.name;
    if (isRenaming) {
      const existing = this.registry.stores.find((s) => s.name === updates.name && s.id !== id);
      if (existing !== undefined) {
        return err(new Error(`Store with name '${updates.name}' already exists`));
      }
    }

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const updated = {
      ...store,
      ...updates,
      updatedAt: new Date(),
    } as Store;

    this.registry.stores[index] = updated;
    await this.saveRegistry();

    // Sync to store definitions if service is available and not skipped
    if (this.definitionService !== undefined && options?.skipDefinitionSync !== true) {
      if (isRenaming) {
        // When renaming: remove old definition and add new one with updated store data
        await this.definitionService.removeDefinition(store.name);
        const newDefinition = this.createDefinitionFromExistingStore(updated);
        // Only add if store type supports definitions (local repo stores don't)
        if (newDefinition !== undefined) {
          await this.definitionService.addDefinition(newDefinition);
        }
      } else {
        // Not renaming: just update description/tags on existing definition
        const defUpdates: { description?: string; tags?: string[] } = {};
        if (updates.description !== undefined) {
          defUpdates.description = updates.description;
        }
        if (updates.tags !== undefined) {
          // Copy tags array to convert from readonly to mutable
          defUpdates.tags = [...updates.tags];
        }
        // Only update if there are actual changes to sync
        if (Object.keys(defUpdates).length > 0) {
          await this.definitionService.updateDefinition(store.name, defUpdates);
        }
      }
    }

    return ok(updated);
  }

  async delete(id: StoreId, options?: OperationOptions): Promise<Result<void>> {
    const index = this.registry.stores.findIndex((s) => s.id === id);
    if (index === -1) {
      return err(new Error(`Store not found: ${id}`));
    }

    const store = this.registry.stores[index];
    if (store === undefined) {
      return err(new Error(`Store not found: ${id}`));
    }

    const storeName = store.name;
    this.registry.stores.splice(index, 1);
    await this.saveRegistry();

    // Sync to store definitions if service is available and not skipped
    if (this.definitionService !== undefined && options?.skipDefinitionSync !== true) {
      await this.definitionService.removeDefinition(storeName);
    }

    return ok(undefined);
  }

  /**
   * Upgrade a schema v1 store to v2 by adding the modelId field.
   * Called after successful reindexing to ensure store is searchable.
   * No-op if store already has modelId (already v2+).
   */
  async upgradeStoreSchema(id: StoreId): Promise<void> {
    await this.ensureRegistryFresh();

    const index = this.registry.stores.findIndex((s) => s.id === id);
    if (index === -1) {
      throw new Error(`Store not found: ${id}`);
    }

    const store = this.registry.stores[index];
    if (store === undefined) {
      throw new Error(`Store not found: ${id}`);
    }

    // Already has modelId - nothing to upgrade
    if (store.modelId) {
      return;
    }

    // Upgrade to current schema
    const upgraded = {
      ...store,
      modelId: this.embeddingModelId,
      schemaVersion: CURRENT_SCHEMA_VERSION,
      updatedAt: new Date(),
    };

    this.registry.stores[index] = upgraded;
    await this.saveRegistry();
  }

  private async loadRegistry(): Promise<void> {
    const registryPath = join(this.dataDir, 'stores.json');
    const exists = await fileExists(registryPath);

    if (!exists) {
      // First run - create empty registry
      this.registry = { stores: [] };
      await this.saveRegistry();
      // Track mtime of newly created file
      const newStats = await stat(registryPath);
      this.registryMtime = newStats.mtimeMs;
      return;
    }

    // File exists - load it (throws on corruption per CLAUDE.md "fail early")
    const content = await readFile(registryPath, 'utf-8');
    try {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      const data = JSON.parse(content) as { stores: (Store | null)[] };

      // Check if any stores need migration (schema < 3 or no pathType on file/repo)
      let migrationNeeded = false;

      this.registry = {
        stores: data.stores
          .filter((s): s is Store => s !== null)
          .map((s) => {
            // Check if this store needs schema migration
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- JSON parsed data may lack field
            if (s.schemaVersion === undefined || s.schemaVersion < CURRENT_SCHEMA_VERSION) {
              migrationNeeded = true;
            }

            // Transform stored path to runtime path (resolve relative paths)
            let store = {
              ...s,
              id: createStoreId(s.id),
              createdAt: new Date(s.createdAt),
              updatedAt: new Date(s.updatedAt),
            };

            // Resolve paths for file and repo stores
            if (store.type === 'file') {
              const resolvedPath = this.resolveStoredPath(store.path, store.pathType);
              store = { ...store, path: resolvedPath };
            } else if (store.type === 'repo') {
              const resolvedPath = this.resolveStoredPath(store.path, store.pathType);
              store = { ...store, path: resolvedPath };
            }

            return store;
          }),
      };

      // Set migration flag for lazy upgrade on next save
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- set inside map callback
      if (migrationNeeded) {
        logger.debug('Schema migration needed - will upgrade on next save');
        this.needsMigration = true;
      }
    } catch (error) {
      throw new Error(
        `Failed to parse store registry at ${registryPath}: ${error instanceof Error ? error.message : String(error)}`
      );
    }

    // Track mtime after successful load
    const loadedStats = await stat(registryPath);
    this.registryMtime = loadedStats.mtimeMs;
  }

  private async saveRegistry(): Promise<void> {
    const registryPath = join(this.dataDir, 'stores.json');

    // Convert runtime stores to stored format (with relative paths where applicable)
    const storedStores = this.registry.stores.map((store) => {
      // Handle schema migration if needed
      const schemaVersion = this.needsMigration ? CURRENT_SCHEMA_VERSION : store.schemaVersion;

      if (store.type === 'file') {
        const { path: storedPath, pathType } = this.toStoredPath(store.path, false);
        return {
          ...store,
          path: storedPath,
          pathType,
          schemaVersion,
        };
      } else if (store.type === 'repo') {
        // Cloned repos (with URL) are managed by BK - always absolute
        // Local repos (no URL) are user-provided - make portable
        const isClonedRepo = store.url !== undefined;
        const { path: storedPath, pathType } = this.toStoredPath(store.path, isClonedRepo);
        return {
          ...store,
          path: storedPath,
          pathType,
          schemaVersion,
        };
      } else {
        // Web stores don't have paths
        return {
          ...store,
          schemaVersion,
        };
      }
    });

    // Clear migration flag after save
    if (this.needsMigration) {
      logger.info('Schema migration complete - upgraded to v3');
      this.needsMigration = false;
    }

    await atomicWriteFile(registryPath, JSON.stringify({ stores: storedStores }, null, 2));
  }
}
